import "../../styles/bootstrap/bootstrap-popover.scss";
import "../../styles/popoverOverride.scss";

import { Map } from "immutable";
import PropTypes from "prop-types";
import React, { createRef, Fragment } from "react";
import { PopoverBody } from "reactstrap";

import keyConstants from "../../constants/keyConstants";
import { isChildInParentView } from "../../utils/domUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import NavigationToolbar from "../navigationToolbar";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import { withScreenContext } from "../screen/ScreenContext";
import FormListButtonWrapper from "./FormListButtonWrapper";

const DEFAULT_OPTIONS_IN_ROW = 4;

const isMobile = preprocessorQuery.isMobile();
const isTablet = preprocessorQuery.isTablet();

class FormGridOptionsPicker extends React.Component {
  constructor(props) {
    super(props);

    this.toggle = this.toggle.bind(this);
    this.optionClick = this.optionClick.bind(this);
    this.onPopoverKeyDown = this.onPopoverKeyDown.bind(this);
    this.state = {
      popoverOpen: false,
      selectedValue: null
    };
    this._popoverDom = createRef();
    this._optionsRefMap = null;
  }

  componentDidUpdate(prevProps, prevState) {
    // If opened the popover,
    // auto focus the color menu
    //#!if iOS !== true
    if (!prevState.popoverOpen && this.state.popoverOpen && this._popoverDom && this._popoverDom.current) {
      this._popoverDom.current.focus();
    }
    //#!endif
  }

  getOptionKey(opt) {
    const { optionKeys } = this.props;
    return optionKeys && optionKeys.length > 0 ? optionKeys.map(k => opt[k]).join("-") : opt;
  }

  createOptionsRefMap() {
    this._optionsRefMap =
      this._optionsRefMap ||
      this.props.options.reduce((refMap, opt) => {
        return refMap.set(this.getOptionKey(opt), createRef());
      }, Map());
  }

  toggle() {
    this.setState({
      popoverOpen: !this.state.popoverOpen,
      selectedValue: this.state.popoverOpen ? this.state.selectedValue : this.props.value
    });
    if (!this.state.popoverOpen) {
      this.createOptionsRefMap();
    }
  }

  optionClick(opt) {
    this.toggle();
    this.props.onChange(opt);
  }

  renderOptions() {
    return this.props.options.map(opt => {
      return this.props.optionRenderer(
        opt,
        this.state.selectedValue,
        () => this.optionClick(opt),
        this._optionsRefMap.get(this.getOptionKey(opt)),
        this.props.largeIcons
      );
    });
  }

  doesOptionEqualToValue(option, value) {
    // Compare the keys
    const { optionKeys } = this.props;
    if (optionKeys && optionKeys.length > 0) {
      return optionKeys.every(key => option[key] === value[key]);
    } else {
      return option === value;
    }
  }

  scrollToOption(opt) {
    const optionRef = this._optionsRefMap.get(this.getOptionKey(opt));
    if (
      optionRef &&
      optionRef.current &&
      this._popoverDom &&
      !isChildInParentView(this._popoverDom.current, optionRef.current)
    ) {
      optionRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }

  onPopoverKeyDown(e) {
    if (
      e.key === keyConstants.Down ||
      e.key === keyConstants.Up ||
      e.key === keyConstants.Left ||
      e.key === keyConstants.Right
    ) {
      e.stopPropagation();
      e.preventDefault();
      const { options, optionsInRow } = this.props;
      let nextOptIndex = options.findIndex(opt => this.doesOptionEqualToValue(opt, this.state.selectedValue));
      const optCount = options.length;
      if (e.key === keyConstants.Right && nextOptIndex < optCount - 1) {
        nextOptIndex += 1;
      } else if (e.key === keyConstants.Left && nextOptIndex > 0) {
        nextOptIndex -= 1;
      } else if (e.key === keyConstants.Up && nextOptIndex >= optionsInRow) {
        nextOptIndex -= optionsInRow;
      } else if (e.key === keyConstants.Down && nextOptIndex < optCount - optionsInRow) {
        nextOptIndex += optionsInRow;
      }
      const newValue = options[nextOptIndex];
      this.setState({ selectedValue: newValue });
      this.scrollToOption(newValue);
    } else if (e.key === keyConstants.Enter) {
      this.optionClick(this.state.selectedValue);
    }
  }

  renderPopoverContent() {
    return this.state.popoverOpen ? (
      <div
        ref={this._popoverDom}
        className={`${this.props.classNamePrefix}Menu`}
        tabIndex={0}
        onKeyDown={this.onPopoverKeyDown}
      >
        {this.renderOptions()}
      </div>
    ) : null;
  }

  renderControl() {
    const arrowIcon = isMobile ? "rightArrow" : "downArrow";

    return (
      <FormListButtonWrapper className={this.props.classNamePrefix} id={this.props.id} onClick={this.toggle}>
        {this.props.controlRenderer(this.props.value)}
        <div className={`${this.props.classNamePrefix}Arrow`}>
          <svg className="icon mono">
            <use xlinkHref={`#${arrowIcon}`} />
          </svg>
        </div>
      </FormListButtonWrapper>
    );
  }

  renderPopover() {
    let placement = "bottom";
    let hideArrow = false;
    let alwaysPortal = false;

    if (isTablet) {
      placement = "bottom-end";
    }

    let popoverOptions = {
      className: `modalPopover`,
      placement,
      hideArrow,
      isOpen: this.state.popoverOpen,
      target: this.props.id,
      toggle: this.toggle
    };

    const child = (
      <Fragment>
        {isMobile ? (
          <div onClick={e => e.stopPropagation()}>
            <NavigationToolbar
              title={this.props.title}
              showBack={true}
              showBackArrow={true}
              onBackClick={this.toggle}
              backText={this.props.backText ? this.props.backText : "Back"}
              flexLeft={1}
              flexCenter={3}
              flexRight={1}
            />
          </div>
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderPopoverContent()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  render() {
    return (
      <div className={`${this.props.classNamePrefix}Wrapper`}>
        {this.renderControl()}
        {this.state.popoverOpen ? this.renderPopover() : null}
      </div>
    );
  }
}

FormGridOptionsPicker.defaultProps = {
  classNamePrefix: "formGridOptionsPicker",
  optionsInRow: DEFAULT_OPTIONS_IN_ROW
};

FormGridOptionsPicker.propTypes = {
  classNamePrefix: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  backText: PropTypes.string,
  controlRenderer: PropTypes.func.isRequired,
  optionRenderer: PropTypes.func.isRequired,
  optionKeys: PropTypes.arrayOf(PropTypes.string),
  optionsInRow: PropTypes.number.isRequired,
  largeIcons: PropTypes.bool,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

let exportedView = FormGridOptionsPicker;
//#!if iOS === true
// exportedView = withScreenContext(FormGridOptionsPicker);
//#!endif
export default exportedView;
